// deriveChatTitle — turns the current chat context (seed + first user
// message) plus a wall-clock timestamp into a stable, human-readable
// label like "Quest: Cold-email a founder · Apr 27 14:32" so the user
// can scan the recent-chats list and identify a run at a glance.
//
// Why this lives in /lib (not in the chat shell): the same algorithm
// is used by (a) the auto-save hook that creates the row, (b) the
// share button that updates an existing row, and (c) the SaveAsPlaybook
// dialog as the default title. Keeping the rule in one place means a
// chat saved by any of those paths reads the same way in the list.
//
// Format rules (in priority order):
//   1. If a seed source identifies a quest/challenge/hack/library
//      entry, the prefix names that context.
//        - "quest:foo"            → "Quest: <title>"
//        - "challenge:bar"        → "Challenge: <title>"
//        - "past-challenge:baz"   → "Challenge: <title>"
//        - "library:fork-to-chat" → "Playbook: <title>"
//   2. Otherwise, take the first user line, normalize whitespace,
//      trim to a readable length.
//   3. If neither is available (empty chat), fall back to "New chat".
//   4. The wall-clock stamp is always appended as " · MMM D HH:mm".

import type { ChatType } from "./chatType";

export interface DeriveChatTitleInput {
  /** Whatever the chat seed bus returned, if anything. */
  seedSource?: string | null;
  /** Title attached to the seed (often the quest/playbook title). */
  seedTitle?: string | null;
  /** First *user* message in the transcript, if any. */
  firstUserLine?: string | null;
  /** Wall-clock to stamp into the title. Defaults to now. */
  at?: Date;
  /** Explicit type chosen by the user in the composer's type picker.
   *  Wins over seed-derived prefixes so picking "Task" labels the chat
   *  even when there is no seed. "open" (or undefined) falls through to
   *  the existing seed → user-line cascade. */
  chatType?: ChatType;
}

const MAX_BODY_LEN = 60;

/** Turn `"library:fork-to-chat"` → `"library"`. */
function seedKindOf(source: string | null | undefined): string | null {
  if (!source) return null;
  const head = source.split(":")[0]?.trim().toLowerCase();
  return head || null;
}

function prefixForKind(kind: string | null): string | null {
  switch (kind) {
    case "quest":
      return "Quest";
    case "challenge":
    case "past-challenge":
      return "Challenge";
    case "hack":
    case "daily-hack":
      return "Hack";
    case "library":
    case "playbook":
      return "Playbook";
    default:
      return null;
  }
}

/** Compact "Apr 27 14:32" — readable at a glance, sortable by eye. */
function formatStamp(d: Date): string {
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${month} ${day} ${hh}:${mm}`;
}

function squash(text: string, max = MAX_BODY_LEN): string {
  const cleaned = text.replace(/\s+/g, " ").trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trimEnd()}…`;
}

export function deriveChatTitle(input: DeriveChatTitleInput): string {
  const at = input.at ?? new Date();
  const stamp = formatStamp(at);

  const explicit = explicitPrefix(input.chatType);
  const prefix = explicit ?? prefixForKind(seedKindOf(input.seedSource));
  const seedBody = input.seedTitle?.trim();
  if (explicit && seedBody) {
    return `${prefix}: ${squash(seedBody)} · ${stamp}`;
  }
  if (explicit) {
    const userBody = input.firstUserLine?.trim();
    if (userBody) return `${prefix}: ${squash(userBody)} · ${stamp}`;
    return `${prefix} · ${stamp}`;
  }
  if (prefix && seedBody) {
    return `${prefix}: ${squash(seedBody)} · ${stamp}`;
  }

  const userBody = input.firstUserLine?.trim();
  if (userBody) {
    return `${squash(userBody)} · ${stamp}`;
  }

  return `New chat · ${stamp}`;
}

/** Same shape, but produces just the body (no timestamp) — handy when
 *  another caller (e.g. the SaveAsPlaybook dialog) wants to show the
 *  derived label without the stamp suffix. */
export function deriveChatTitleBody(
  input: Omit<DeriveChatTitleInput, "at">,
): string {
  const explicit = explicitPrefix(input.chatType);
  const prefix = explicit ?? prefixForKind(seedKindOf(input.seedSource));
  const seedBody = input.seedTitle?.trim();
  if (prefix && seedBody) return `${prefix}: ${squash(seedBody)}`;
  const userBody = input.firstUserLine?.trim();
  if (prefix && userBody) return `${prefix}: ${squash(userBody)}`;
  if (userBody) return squash(userBody);
  if (prefix) return prefix;
  return "New chat";
}

function explicitPrefix(t: ChatType | undefined): string | null {
  switch (t) {
    case "task":
      return "Task";
    case "quest":
      return "Quest";
    case "challenge":
      return "Challenge";
    default:
      return null;
  }
}